// =============================================================================
// MeuDriver — Unified Messages Inbox (student + instructor)
// =============================================================================

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MessageCircle, ArrowLeft, Clock, HelpCircle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getInitials, cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { InquiryChat } from '@/components/shared/InquiryChat'

// ─── Dúvidas antes de agendar (inquiry_messages, 2026-10-12) ─────────────────

interface InquiryThread {
  studentId: string
  instructorId: string
  otherId: string            // instructor_id (aluno vendo) ou student_id (instrutor vendo)
  otherName: string | null
  otherAvatar: string | null
  lastMessage: { content: string; created_at: string; sender_id: string }
  unreadCount: number
}

async function fetchInquiryThreads(userId: string, role: 'student' | 'instructor' | 'admin'): Promise<InquiryThread[]> {
  if (role === 'admin') return []
  let instructorId: string | null = null
  if (role === 'instructor') {
    const { data } = await supabase.from('instructors').select('id').eq('profile_id', userId).maybeSingle()
    instructorId = (data as { id: string } | null)?.id ?? null
    if (!instructorId) return []
  }
  const q = supabase
    .from('inquiry_messages')
    .select('student_id, instructor_id, sender_id, content, created_at, read_at')
    .order('created_at', { ascending: false })
    .limit(500)
  const { data: rows } = role === 'student' ? await q.eq('student_id', userId) : await q.eq('instructor_id', instructorId!)
  if (!rows?.length) return []

  const threads = new Map<string, InquiryThread>()
  for (const m of rows as Array<{ student_id: string; instructor_id: string; sender_id: string; content: string; created_at: string; read_at: string | null }>) {
    const otherId = role === 'student' ? m.instructor_id : m.student_id
    let t = threads.get(otherId)
    if (!t) {
      t = { studentId: m.student_id, instructorId: m.instructor_id, otherId, otherName: null, otherAvatar: null,
            lastMessage: { content: m.content, created_at: m.created_at, sender_id: m.sender_id }, unreadCount: 0 }
      threads.set(otherId, t)
    }
    if (!m.read_at && m.sender_id !== userId) t.unreadCount++
  }

  const ids = [...threads.keys()]
  if (role === 'student') {
    const { data } = await supabase
      .from('instructors')
      .select('id, profile:profiles!instructors_profile_id_fkey(full_name, avatar_url)')
      .in('id', ids)
    for (const r of (data ?? []) as Array<{ id: string; profile: { full_name: string | null; avatar_url: string | null } | null }>) {
      const t = threads.get(r.id)
      if (t) { t.otherName = r.profile?.full_name ?? null; t.otherAvatar = r.profile?.avatar_url ?? null }
    }
  } else {
    const { data } = await supabase.from('profiles').select('id, full_name, avatar_url').in('id', ids)
    for (const r of (data ?? []) as Array<{ id: string; full_name: string | null; avatar_url: string | null }>) {
      const t = threads.get(r.id)
      if (t) { t.otherName = r.full_name; t.otherAvatar = r.avatar_url }
    }
  }
  return [...threads.values()].sort((a, b) => b.lastMessage.created_at.localeCompare(a.lastMessage.created_at))
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Conversation {
  bookingId: string
  scheduledAt: string
  bookingStatus: string
  lastMessage: { content: string; created_at: string; sender_id: string } | null
  unreadCount: number
  otherName: string | null
  otherAvatar: string | null
  detailPath: string
}

// ─── Fetch logic ──────────────────────────────────────────────────────────────

async function fetchConversations(
  userId: string,
  role: 'student' | 'instructor' | 'admin',
): Promise<Conversation[]> {
  // 1. Fetch bookings for this user
  let bookingData: any[] = []

  if (role === 'student') {
    const { data } = await supabase
      .from('bookings')
      .select(`
        id, scheduled_at, status,
        instructor:instructors!bookings_instructor_id_fkey(
          profile:profiles!instructors_profile_id_fkey(full_name, avatar_url)
        )
      `)
      .eq('student_id', userId)
      .order('scheduled_at', { ascending: false })
    bookingData = data ?? []
  } else if (role === 'instructor') {
    // First get the instructors row id
    const { data: instRow } = await supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', userId)
      .maybeSingle()
    if (!instRow) return []

    const { data } = await supabase
      .from('bookings')
      .select(`
        id, scheduled_at, status,
        student:profiles!bookings_student_id_fkey(full_name, avatar_url)
      `)
      .eq('instructor_id', instRow.id)
      .order('scheduled_at', { ascending: false })
    bookingData = data ?? []
  }

  if (bookingData.length === 0) return []
  const bookingIds = bookingData.map((b: any) => b.id)

  // 2. Fetch all messages for those bookings
  const { data: messages } = await supabase
    .from('messages')
    .select('booking_id, content, created_at, sender_id, read_at')
    .in('booking_id', bookingIds)
    .order('created_at', { ascending: false })

  if (!messages || messages.length === 0) return []

  // 3. Group by booking_id
  const bookingMap = new Map(bookingData.map((b: any) => [b.id, b]))
  const msgByBooking = new Map<string, typeof messages>()
  for (const m of messages) {
    if (!msgByBooking.has(m.booking_id)) msgByBooking.set(m.booking_id, [])
    msgByBooking.get(m.booking_id)!.push(m)
  }

  // 4. Build conversation list
  const convos: Conversation[] = []
  for (const [bookingId, msgs] of msgByBooking) {
    const booking = bookingMap.get(bookingId)
    if (!booking) continue

    const lastMsg = msgs[0]
    const unread = msgs.filter((m) => !m.read_at && m.sender_id !== userId).length

    const other =
      role === 'student'
        ? (booking.instructor as any)?.profile
        : (booking.student as any)

    convos.push({
      bookingId,
      scheduledAt: booking.scheduled_at,
      bookingStatus: booking.status,
      lastMessage: lastMsg,
      unreadCount: unread,
      otherName: other?.full_name ?? null,
      otherAvatar: other?.avatar_url ?? null,
      detailPath:
        role === 'student'
          ? `/aluno/aulas/${bookingId}`
          : `/instrutor/aulas?chat=${bookingId}`,
    })
  }

  convos.sort(
    (a, b) =>
      new Date(b.lastMessage!.created_at).getTime() -
      new Date(a.lastMessage!.created_at).getTime(),
  )

  return convos
}

// ─── Conversation row ─────────────────────────────────────────────────────────

function ConvoRow({ c, role }: { c: Conversation; role: string }) {
  const timeAgo = c.lastMessage
    ? formatDistanceToNow(parseISO(c.lastMessage.created_at), {
        addSuffix: true,
        locale: ptBR,
      })
    : ''

  const name = c.otherName ?? (role === 'student' ? 'Instrutor' : 'Aluno')

  return (
    <Link
      to={c.detailPath}
      className={cn(
        'flex items-center gap-4 px-4 sm:px-6 py-4 border-b border-gray-100 hover:bg-muted/30 transition-colors',
        c.unreadCount > 0 && 'bg-brand-50/40 hover:bg-brand-50/60',
      )}
    >
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11">
          <AvatarImage src={c.otherAvatar ?? undefined} />
          <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold text-sm">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        {c.unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[9px] font-bold text-white">
            {c.unreadCount > 9 ? '9+' : c.unreadCount}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={cn('text-sm truncate', c.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700')}>
            {name}
          </p>
          <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo}</span>
        </div>
        <p className={cn('text-xs mt-0.5 truncate', c.unreadCount > 0 ? 'text-gray-700' : 'text-muted-foreground')}>
          {c.lastMessage?.content ?? ''}
        </p>
      </div>

      {c.unreadCount > 0 && (
        <Badge className="shrink-0 bg-brand-500 text-white text-[10px] px-1.5 py-0.5">
          {c.unreadCount}
        </Badge>
      )}
    </Link>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MessagesPage() {
  const { user, profile } = useAuth()
  const role = (profile?.role ?? 'student') as 'student' | 'instructor' | 'admin'
  const backPath = role === 'instructor' ? '/instrutor/dashboard' : '/aluno/dashboard'

  const { data: conversations, isLoading } = useQuery<Conversation[]>({
    queryKey: ['conversations', user?.id, role],
    enabled: !!user?.id,
    queryFn: () => fetchConversations(user!.id, role),
    staleTime: 1000 * 30,
  })

  const { data: threads = [] } = useQuery<InquiryThread[]>({
    queryKey: ['inquiry-threads', user?.id, role],
    enabled: !!user?.id,
    queryFn: () => fetchInquiryThreads(user!.id, role),
    staleTime: 1000 * 15,
    refetchInterval: 30_000,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const [openThread, setOpenThread] = useState<InquiryThread | null>(null)
  // Aviso "Nova dúvida" chega com ?duvida=<id da outra parte>: abre a conversa.
  const duvida = searchParams.get('duvida')
  useEffect(() => {
    if (!duvida || !threads.length) return
    const t = threads.find((x) => x.otherId === duvida)
    if (t) setOpenThread(t)
  }, [duvida, threads])

  const totalUnread = (conversations?.reduce((s, c) => s + c.unreadCount, 0) ?? 0)
    + threads.reduce((s, t) => s + t.unreadCount, 0)

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to={backPath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Mensagens
          </h1>
          {totalUnread > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {totalUnread} mensagem{totalUnread > 1 ? 'ens' : ''} não lida{totalUnread > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Dúvidas antes de agendar */}
      {threads.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
          <p className="flex items-center gap-1.5 border-b border-gray-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-6">
            <HelpCircle className="h-3.5 w-3.5" /> Dúvidas antes de agendar
          </p>
          {threads.map((t) => {
            const name = t.otherName ?? (role === 'student' ? 'Instrutor' : 'Aluno')
            return (
              <button key={t.otherId} type="button" onClick={() => setOpenThread(t)}
                className={cn('flex w-full items-center gap-4 border-b border-gray-100 px-4 py-4 text-left transition-colors hover:bg-muted/30 sm:px-6',
                  t.unreadCount > 0 && 'bg-brand-50/40')}>
                <Avatar className="h-11 w-11 shrink-0">
                  <AvatarImage src={t.otherAvatar ?? undefined} />
                  <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold text-sm">{getInitials(name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={cn('truncate text-sm', t.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700')}>{name}</p>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {formatDistanceToNow(parseISO(t.lastMessage.created_at), { addSuffix: true, locale: ptBR })}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{t.lastMessage.content}</p>
                </div>
                {t.unreadCount > 0 && (
                  <Badge className="shrink-0 bg-brand-500 px-1.5 py-0.5 text-[10px] text-white">{t.unreadCount}</Badge>
                )}
              </button>
            )
          })}
        </div>
      )}

      <Dialog open={!!openThread} onOpenChange={(o) => {
        if (!o) {
          setOpenThread(null)
          if (searchParams.get('duvida')) { const n = new URLSearchParams(searchParams); n.delete('duvida'); setSearchParams(n, { replace: true }) }
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{openThread?.otherName ?? 'Conversa'}</DialogTitle>
          </DialogHeader>
          {openThread && (
            <InquiryChat studentId={openThread.studentId} instructorId={openThread.instructorId}
              otherName={openThread.otherName?.split(' ')[0] ?? (role === 'student' ? 'o instrutor' : 'o aluno')} />
          )}
        </DialogContent>
      </Dialog>

      {/* Content */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : (!conversations || conversations.length === 0) && threads.length > 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma conversa de aula ainda.</p>
        ) : !conversations || conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
              <MessageCircle className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-700">Nenhuma conversa ainda</p>
            <p className="text-sm text-muted-foreground mt-1">
              {role === 'student'
                ? 'Use "Tirar dúvida" no perfil de um instrutor para conversar antes de agendar. As conversas das aulas também aparecem aqui.'
                : 'Dúvidas de alunos e as conversas das aulas aparecem aqui.'}
            </p>
          </div>
        ) : (
          <div>
            {conversations.map((c) => (
              <ConvoRow key={c.bookingId} c={c} role={role} />
            ))}
            <div className="flex items-center justify-center gap-1.5 py-3 bg-gray-50">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[11px] text-muted-foreground">
                {conversations.length} conversa{conversations.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
